<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="560"
    persistent
    overlay-color="#0f172a"
    overlay-opacity="0.65"
    content-class="ism-cropper-dialog"
  >
    <div class="ism-cropper-wrap">

      <!-- Header -->
      <div class="ism-cropper-header">
        <span class="ism-cropper-title">{{ title }}</span>
        <button class="ism-cropper-close" @click="cancel" type="button">
          <v-icon size="18" color="#94a3b8">mdi-close</v-icon>
        </button>
      </div>

      <!-- Cropper area -->
      <div class="ism-cropper-body">
        <Cropper
          ref="cropper"
          class="ism-cropper-canvas"
          :src="imageSrc"
          :stencil-size="stencilSize"
          :stencil-props="stencilProps"
          :stencil-component="stencilComponent"
          background-class="ism-cropper-bg"
          :image-restriction="imageRestriction"
        />
      </div>

      <div class="ism-cropper-controls">
        <button class="ism-crop-ctrl-btn" @click="zoom(-0.1)" type="button" title="Zoom Out">
          <v-icon size="18">mdi-magnify-minus-outline</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="zoom(0.1)" type="button" title="Zoom In">
          <v-icon size="18">mdi-magnify-plus-outline</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="rotate(-90)" type="button" title="Rotate Left">
          <v-icon size="18">mdi-rotate-left</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="rotate(90)" type="button" title="Rotate Right">
          <v-icon size="18">mdi-rotate-right</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="flip(true, false)" type="button" title="Flip Horizontal">
          <v-icon size="18">mdi-flip-horizontal</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="flip(false, true)" type="button" title="Flip Vertical">
          <v-icon size="18">mdi-flip-vertical</v-icon>
        </button>
      </div>

      <!-- Footer -->
      <div class="ism-cropper-footer">
        <button class="ism-crop-btn-cancel" @click="cancel" type="button">Cancel</button>
        <button class="ism-crop-btn-apply" @click="applyCrop" type="button">
          <v-icon size="15" style="margin-right:5px;">mdi-crop</v-icon>
          Apply Crop
        </button>
      </div>

    </div>
  </v-dialog>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  name: 'ImageCropper',
  components: { Cropper },

  props: {
    value:    { type: Boolean, default: false },
    imageSrc: { type: String,  default: '' },
    title:    { type: String,  default: 'Crop Image' },
    // 'circle' for profile pics, 'square' for logos
    shape:    { type: String,  default: 'circle', validator: v => ['circle', 'square'].includes(v) },
    // Output size in px
    outputSize: { type: Number, default: 400 },
  },

  emits: ['input', 'cropped'],

  computed: {
    stencilComponent() {
      return this.shape === 'circle'
        ? 'circle-stencil'
        : 'rectangle-stencil';
    },
    stencilProps() {
      const common = { aspectRatio: 1 };

      return this.shape === 'circle'
        ? common
        : {
            ...common,
            movable: false,
            resizable: false,
            handlers: {},
            lines: {},
          };
    },
    stencilSize() {
      if (this.shape !== 'square') return null;

      return ({ boundaries }) => {
        const size = Math.min(300, Math.floor(Math.min(boundaries.width, boundaries.height) * 0.78));
        return { width: size, height: size };
      };
    },
    imageRestriction() {
      return this.shape === 'square' ? 'none' : 'stencil';
    },
  },

  methods: {
    zoom(delta) {
      this.$refs.cropper.zoom(1 + delta);
    },
    rotate(deg) {
      this.$refs.cropper.rotate(deg);
    },
    flip(h, v) {
      this.$refs.cropper.flip(h, v);
    },

    cancel() {
      this.$emit('input', false);
    },

    applyCrop() {
      const { canvas } = this.$refs.cropper.getResult();
      if (!canvas) return;

      // Resize to outputSize
      const size = this.outputSize;
      const resized = document.createElement('canvas');
      resized.width  = size;
      resized.height = size;
      const ctx = resized.getContext('2d');
      ctx.drawImage(canvas, 0, 0, size, size);

      resized.toBlob(blob => {
        if (!blob) return;
        const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(blob);
        this.$emit('cropped', { file, previewUrl });
        this.$emit('input', false);
      }, 'image/jpeg', 0.92);
    },
  },
};
</script>

<style>
/* Non-scoped — targets v-dialog content-class */
.ism-cropper-dialog {
  border-radius: 18px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(0,0,0,0.28) !important;
}
</style>

<style scoped>
/* ── Wrapper ─────────────────────────────── */
.ism-cropper-wrap {
  display: flex;
  flex-direction: column;
  background: var(--card-bg, #fff);
  border-radius: 18px;
  overflow: hidden;
  font-family: "Inter", sans-serif;
}

/* ── Header ──────────────────────────────── */
.ism-cropper-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--sidebar-bg, #1B2340);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.ism-cropper-title {
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
  letter-spacing: -0.01em;
}
.ism-cropper-close {
  width: 28px; height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.06);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.18s;
}
.ism-cropper-close:hover {
  background: rgba(255,255,255,0.12);
  transform: rotate(90deg);
}

/* ── Cropper canvas ──────────────────────── */
.ism-cropper-body {
  background: #0f172a;
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.ism-cropper-canvas {
  width: 100%;
  height: 100%;
}

/* ── Controls ────────────────────────────── */
.ism-cropper-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background: var(--bg-primary, #f1f5f9);
  border-top: 1px solid var(--border-color, rgba(0,0,0,0.07));
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.07));
}
.ism-crop-ctrl-btn {
  width: 36px; height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  background: var(--card-bg, #fff);
  color: var(--text-secondary, #64748b);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s;
}
.ism-crop-ctrl-btn:hover {
  background: var(--accent-lt, rgba(242,101,34,0.08));
  color: var(--accent, #F26522);
  border-color: var(--accent, #F26522);
}

/* ── Footer ──────────────────────────────── */
.ism-cropper-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: var(--sidebar-bg, #1B2340);
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.ism-crop-btn-cancel {
  padding: 9px 18px;
  border-radius: 9px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  color: #94a3b8;
  font-family: "Inter", sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.18s;
}
.ism-crop-btn-cancel:hover {
  background: rgba(255,255,255,0.1);
  color: #f1f5f9;
}
.ism-crop-btn-apply {
  display: inline-flex; align-items: center;
  padding: 9px 20px;
  border-radius: 9px; border: none;
  background: linear-gradient(135deg, #F26522, #C94F10);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(242,101,34,0.35);
}
.ism-crop-btn-apply:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.ism-cropper-wrap {
  background: #ffffff;
  border-radius: 16px;
}

.ism-cropper-header {
  padding: 16px 20px 12px;
  background: #ffffff;
  border-bottom: 1px solid #eef2f7;
}

.ism-cropper-title {
  font-size: 16px;
  color: #1e293b;
  letter-spacing: 0;
}

.ism-cropper-close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0;
  background: transparent;
}

.ism-cropper-close:hover {
  background: #f1f5f9;
  transform: none;
}

.ism-cropper-body {
  background: #000000;
}

.ism-cropper-canvas ::v-deep .vue-advanced-cropper__background,
.ism-cropper-canvas ::v-deep .vue-advanced-cropper__foreground {
  background: #000000 !important;
}

.ism-cropper-canvas ::v-deep .vue-advanced-cropper__stencil {
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.42);
}

.ism-cropper-canvas ::v-deep .vue-rectangle-stencil__preview,
.ism-cropper-canvas ::v-deep .vue-circle-stencil__preview {
  border: 3px solid #ff3b30 !important;
  box-sizing: border-box;
}

.ism-cropper-controls {
  padding: 12px 20px;
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}

.ism-crop-ctrl-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
}

.ism-crop-ctrl-btn:hover {
  background: rgba(242,101,34,0.08);
  color: #F26522;
  border-color: #F26522;
}

.ism-cropper-footer {
  justify-content: flex-start;
  background: #ffffff;
  border-top: 1px solid #eef2f7;
}

.ism-crop-btn-cancel {
  border: 0;
  background: transparent;
}

.ism-crop-btn-cancel:hover {
  background: #f8fafc;
  color: #64748b;
}

@media (max-width: 600px) {
  .ism-cropper-body {
    height: 300px;
  }

  .ism-cropper-controls {
    flex-wrap: wrap;
  }
}
</style>
